import type { MovementPattern } from '@jackedlogs/shared';

// Single source for mapping a catalog Exercise to the DraftEntry logging-kind
// flags. Every entry-create/replace path — add_exercise (useAddExercise),
// plan-starter hydrate (planStarterHydration), and exercise swap
// (useReplaceExercise) — derives them the SAME way, so they can't
// disagree on how an exercise's catalog row becomes isCardio/isTimeBased/
// isBodyweight. A divergence caused IB-2: the swap ignored isTimeBased, so an
// isometric hold logged as reps×weight instead of a timed hold.
//
// The param is loose (all fields optional) so callers with a possibly-missing
// catalog row (plan-starter) can pass `ex?.field` and a pre-resolved
// movementPattern fallback; callers with a definite Exercise pass it whole.
type EntryKindFlags = {
  isBodyweight: boolean;
  isCardio: boolean;
  isTimeBased: boolean;
  movementPattern: MovementPattern | null;
};

export function deriveEntryKind(ex: {
  isBodyweight?: boolean | null;
  movementPattern?: MovementPattern | null;
  isTimeBased?: boolean | null;
}): EntryKindFlags {
  const movementPattern = ex.movementPattern ?? null;
  return {
    isBodyweight: ex.isBodyweight === true,
    isCardio: movementPattern === 'cardio',
    isTimeBased: ex.isTimeBased === true,
    movementPattern,
  };
}

// Laterality seed for an entry — the catalog `unilateral` flag drives the
// ×2 multiplier, cardio never doubles. Extracted here first (IB-8, inbox
// 5aa7017d: the swap never seeded it), then lifted to shared setVolume.ts
// in Slice 2 so the API's save-time stamp (setFactory.buildLogSet) can't
// diverge from the logger's seed. Re-exported so the logger callsites
// (useAddExercise / planStarterHydration / useReplaceExercise) keep importing
// their entry-derivation rules from this one file.
export { catalogLoadedSides } from '@jackedlogs/shared';

// The primary logged metric splits kinds into two families: timed/cardio log a
// DURATION, everything else logs REPS. A swap that crosses this line (IB-6)
// orphans the set's already-typed field — a completed weighted set (reps) swept
// to a timed exercise (duration) would be silently dropped on save — so the
// swap path warns + resets the sets instead of losing them quietly.
export function kindNeedsDuration(f: {
  isCardio: boolean;
  isTimeBased: boolean;
}): boolean {
  return f.isCardio || f.isTimeBased;
}
